const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]
const IHDR_COLOR_TYPE_OFFSET = 25
const COLOR_TYPE_RGB = 2
const CRC_TABLE = (() => {
  const table = new Uint32Array(256)
  for (let n = 0; n < 256; n++) {
    let c = n
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    table[n] = c >>> 0
  }
  return table
})()

const crc32 = (bytes: Uint8Array): number => {
  let c = 0xffffffff
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8)
  return (c ^ 0xffffffff) >>> 0
}

const canvasToPngBlob = (canvas: HTMLCanvasElement): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob)
      else reject(new Error('canvas.toBlob returned null'))
    }, 'image/png')
  })

/** True when the PNG header says colour type 2 (RGB, no alpha channel). */
const isRgbPng = async (blob: Blob): Promise<boolean> => {
  const head = new Uint8Array(await blob.slice(0, IHDR_COLOR_TYPE_OFFSET + 1).arrayBuffer())
  return head.length > IHDR_COLOR_TYPE_OFFSET && PNG_SIGNATURE.every((b, i) => head[i] === b)
    ? head[IHDR_COLOR_TYPE_OFFSET] === COLOR_TYPE_RGB
    : false
}

const buildChunk = (type: string, data: Uint8Array): Uint8Array => {
  const chunk = new Uint8Array(12 + data.length)
  const view = new DataView(chunk.buffer)
  view.setUint32(0, data.length)
  for (let i = 0; i < 4; i++) chunk[4 + i] = type.charCodeAt(i)
  chunk.set(data, 8)
  view.setUint32(8 + data.length, crc32(chunk.subarray(4, 8 + data.length)))
  return chunk
}

const ADLER_MOD = 65521
const STORED_BLOCK_MAX = 0xffff

const adler32 = (bytes: Uint8Array): number => {
  let a = 1
  let b = 0
  for (let i = 0; i < bytes.length; i++) {
    a = (a + bytes[i]) % ADLER_MOD
    b = (b + a) % ADLER_MOD
  }
  return ((b << 16) | a) >>> 0
}

/** zlib stream made of uncompressed (stored) blocks — larger, but valid, for browsers without CompressionStream. */
const storedZlib = (raw: Uint8Array): Uint8Array => {
  const blocks = Math.max(1, Math.ceil(raw.length / STORED_BLOCK_MAX))
  const out = new Uint8Array(2 + raw.length + blocks * 5 + 4)
  const view = new DataView(out.buffer)
  out[0] = 0x78
  out[1] = 0x01
  let pos = 2
  for (let block = 0; block < blocks; block++) {
    const start = block * STORED_BLOCK_MAX
    const length = Math.min(STORED_BLOCK_MAX, raw.length - start)
    out[pos] = block === blocks - 1 ? 1 : 0
    view.setUint16(pos + 1, length, true)
    view.setUint16(pos + 3, ~length & 0xffff, true)
    out.set(raw.subarray(start, start + length), pos + 5)
    pos += 5 + length
  }
  view.setUint32(pos, adler32(raw))
  return out
}

const deflate = async (raw: Uint8Array): Promise<Uint8Array> => {
  if (typeof CompressionStream === 'undefined') return storedZlib(raw)
  const stream = new Blob([raw as BlobPart]).stream().pipeThrough(new CompressionStream('deflate'))
  return new Uint8Array(await new Response(stream).arrayBuffer())
}

/** Re-encodes the canvas as a 24-bit RGB PNG (no alpha channel) — hand-rolled like bmpEncoder/icoEncoder. */
const encodeRgbPng = async (canvas: HTMLCanvasElement): Promise<Blob> => {
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('2d context unavailable')
  const {width, height} = canvas
  const rgba = ctx.getImageData(0, 0, width, height).data
  const rowBytes = width * 3
  const raw = new Uint8Array((rowBytes + 1) * height)
  for (let y = 0; y < height; y++) {
    const out = y * (rowBytes + 1)
    raw[out] = 0
    for (let x = 0; x < width; x++) {
      const src = (y * width + x) * 4
      const dst = out + 1 + x * 3
      raw[dst] = rgba[src]
      raw[dst + 1] = rgba[src + 1]
      raw[dst + 2] = rgba[src + 2]
    }
  }
  const ihdr = new Uint8Array(13)
  const ihdrView = new DataView(ihdr.buffer)
  ihdrView.setUint32(0, width)
  ihdrView.setUint32(4, height)
  ihdr[8] = 8
  ihdr[9] = COLOR_TYPE_RGB
  const parts = [
    new Uint8Array(PNG_SIGNATURE),
    buildChunk('IHDR', ihdr),
    buildChunk('IDAT', await deflate(raw)),
    buildChunk('IEND', new Uint8Array(0)),
  ]
  return new Blob(parts as BlobPart[], {type: 'image/png'})
}

/**
 * App Store Connect rejects creative assets with an alpha channel. Canvases created with
 * `alpha: false` usually encode as RGB already; if the browser still emits RGBA, re-encode.
 */
export const canvasToOpaquePng = async (canvas: HTMLCanvasElement): Promise<Blob> => {
  const blob = await canvasToPngBlob(canvas)
  if (await isRgbPng(blob)) return blob
  return encodeRgbPng(canvas)
}
